import type { RefObject } from "react";
import {
  WebsiteContainer,
  WebsiteGlow,
  WebsiteHeader,
  WebsiteHeading,
  WebsiteStoreBadges,
  WebsiteText,
} from "@-/dev.improve.haniverse.components.website";
import { LandingAndroidFrame, LandingIphoneFrame } from "#landing-device-frames";
import { LANDING_DOWNLOAD_SECTION_ID } from "#landing-download-section-id";
import { LandingQrCodeCard } from "#landing-download-qr-code";
import { LANDING_SCREENSHOTS } from "#landing-screenshots";

/** A character's story on the iPhone in front, and one component turning up across a search behind it. */
function LandingHeroDevices() {
  return (
    <div
      aria-label="Haniverse running on an Android phone and an iPhone"
      className="relative mx-auto aspect-100/104 w-full max-w-[34rem] lg:max-w-none"
      role="img"
    >
      <WebsiteGlow blur="md" className="inset-[8%_4%_10%]" tone="medium" />
      <LandingAndroidFrame
        className="absolute top-[7%] left-[6%] w-[45%] rotate-[-7deg]"
        priority
        screenshot={LANDING_SCREENSHOTS.androidSearch}
      />
      <LandingIphoneFrame
        className="absolute top-0 right-[6%] w-[48%] rotate-3"
        priority
        screenshot={LANDING_SCREENSHOTS.iosMnemonic}
      />
    </div>
  );
}

/**
 * Everything a visitor needs to decide is above the fold: Haniverse's two promises, real screens
 * from both phones, and the two store badges. There is no web call to action here by design.
 */
export function LandingHero({
  callToActionRef,
}: {
  callToActionRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <WebsiteHeader downloadHref={`#${LANDING_DOWNLOAD_SECTION_ID}`} homeHref="#top">
      <WebsiteContainer className="grid items-center gap-x-12 gap-y-10 pt-4 pb-0 sm:pt-8 lg:min-h-[calc(100svh-5.5rem)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:pt-0 lg:pb-10">
        <div className="relative z-10 max-w-[36rem]">
          <p className="m-0 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-3 py-1.5 text-xs font-semibold text-black/60 backdrop-blur">
            <span className="size-1.5 rounded-full bg-website-accent" />
            The Chinese character app for iPhone &amp; Android
          </p>
          <WebsiteHeading className="mt-5" level={1} size="display">
            Chinese characters that stick.{" "}
            <span className="text-website-accent">Lessons that add up.</span>
          </WebsiteHeading>
          <WebsiteText className="mt-4 max-w-[32rem] sm:mt-5" size="lead">
            Haniverse gives every character a short, vivid story, so it’s easy to recall, spot and
            write. And it never teaches anything before its parts: each lesson, down to your first
            pinyin tone, is made from what you’ve already learned.
          </WebsiteText>

          <div
            className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-5 sm:mt-8"
            ref={callToActionRef}
          >
            <WebsiteStoreBadges />
            <LandingQrCodeCard className="hidden lg:flex" />
          </div>
          <WebsiteText className="mt-4 sm:mt-5" size="fine" tone="subtle">
            Free to start · Simplified &amp; Traditional · HSK 1 and up
          </WebsiteText>
        </div>

        <div className="relative mb-[-22%] sm:mb-[-14%] lg:mb-0">
          <LandingHeroDevices />
        </div>
      </WebsiteContainer>
    </WebsiteHeader>
  );
}
